import { useEffect, useRef, useState } from "react";

import { fetchFeaturedCities } from "./metadata";

type UseFeaturedCitiesOptions = {
  limit?: number;
  around?: string | null;
  /**
   * Liste déjà résolue par le rendu serveur.
   *
   * Sans elle, le premier rendu retombe sur le repli de `useMajorCities` — les
   * douze villes les plus peuplées — et c'est ce repli qui partait dans le HTML
   * servi aux crawlers.
   */
  initialData?: string[] | null;
};

export function useFeaturedCities(options?: UseFeaturedCitiesOptions) {
  // Un tableau vide n'est pas une graine : le serveur l'émet sur les pages
  // sans ancrage géographique. Le traiter comme telle laisserait `loading` à
  // faux pendant la requête et empêcherait l'appel de rafraîchissement.
  const seed =
    options?.initialData && options.initialData.length > 0
      ? options.initialData
      : null;
  // Conservé dans une ref : seul le tout premier passage de l'effet doit
  // épargner l'état initial. Tout changement d'ancrage ensuite repart d'une
  // requête normale.
  const hasSeededRef = useRef(seed !== null);
  const [data, setData] = useState<string[] | null>(seed);
  const [loading, setLoading] = useState(seed === null);
  const [error, setError] = useState<string | null>(null);
  const limit = options?.limit ?? 12;
  const around = options?.around ?? null;

  useEffect(() => {
    let isMounted = true;

    // La graine serveur fait déjà foi : refaire l'appel au montage ne
    // changerait rien à l'affichage et coûterait une requête par page.
    if (hasSeededRef.current) {
      hasSeededRef.current = false;
      setLoading(false);

      return () => {
        isMounted = false;
      };
    }

    fetchFeaturedCities(limit, around)
      .then((cities) => {
        if (!isMounted) return;
        setData(cities);
        setError(null);
      })
      .catch(() => {
        if (!isMounted) return;
        setError("Impossible de charger les villes.");
        setData(null);
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
    // `initialData` est volontairement absent : il ne décrit que l'état de
    // départ, le relire relancerait l'effet à chaque rendu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [around, limit]);

  return { data, loading, error };
}
